import React, { useState, useEffect } from 'react';
import { Container, Card, CardBody, Button, Nav, NavItem, NavLink, Input } from 'reactstrap';
import Breadcrumbs from '../../layout/breadcrumb';
import { PlusCircle, BarChart2, List, RefreshCw } from 'react-feather';
import MealPassReport from './MealPassReport';
import MealPassList from './MealPassList';
import CreateMealPassModal from './CreateMealPassModal';
import MealPassDetailModal from './MealPassDetailModal';
import { Get_Meal_Time_Slots } from '../../api/MealPassApi/MealPassApi';
import { Temple_Trust_List } from '../../api/TempleTrustApi/TempleTrustApi';
import './MealPass.css';

const MealPass = () => {
  const [activeTab, setActiveTab] = useState('report'); // 'report' | 'list'
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createdPass, setCreatedPass] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [activeSlotInfo, setActiveSlotInfo] = useState(null);
  const [trustList, setTrustList] = useState([]);
  const [selectedTrustId, setSelectedTrustId] = useState('');

  const fetchTrusts = async () => {
    try {
      const { data } = await Temple_Trust_List({ showAll: true });
      if (data?.status === 200 && data?.payload?.data) {
        const list = data.payload.data;
        setTrustList(list);
        if (list.length === 1) {
          setSelectedTrustId(String(list[0].trustId));
        }
      }
    } catch (err) {
      console.error('Error fetching temple trust list:', err);
    }
  };

  const fetchActiveSlot = async () => {
    try {
      const { data } = await Get_Meal_Time_Slots();
      if (data?.payload?.slots) {
        const liveSlot = data.payload.slots.find((s) => s.isActiveNow);
        if (liveSlot) {
          const slotLabel =
            liveSlot.type === 'breakFast'
              ? 'Breakfast Window (अल्पाहार)'
              : liveSlot.type === 'lunch'
              ? 'Lunch Window (दोपहर भोजन)'
              : 'Dinner Window (रात्रि भोजन)';
          setActiveSlotInfo({
            name: slotLabel,
            time: `${liveSlot.startTime} - ${liveSlot.endTime}`,
            isActive: true
          });
        } else {
          setActiveSlotInfo({
            name: 'Bhojanshala Currently Closed (भोजनशाला बंद)',
            isActive: false
          });
        }
      }
    } catch (err) {
      console.error('Error fetching active meal slot:', err);
    }
  };

  useEffect(() => {
    fetchTrusts();
    fetchActiveSlot();
    const interval = setInterval(fetchActiveSlot, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  const triggerRefresh = () => {
    setRefreshSignal((prev) => prev + 1);
    fetchActiveSlot();
  };

  useEffect(() => {
    if (!createModalOpen && !detailModalOpen) {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    }
  }, [createModalOpen, detailModalOpen]);

  return (
    <div className="meal-pass-container">
      <Breadcrumbs title="Bhojan Pass (भोजन पास)" parent="Dashboard" />
      <Container fluid={true}>
        {/* Top Control Header Card */}
        <Card className="meal-card-kpi mb-3">
          <CardBody className="p-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 meal-pass-top-bar">
              <div className="d-flex align-items-center gap-3">
                <div style={{ fontSize: '32px' }}>🍲</div>
                <div>
                  <h4 className="fw-bold mb-1 text-dark meal-pass-title">Bhojan Pass Management (भोजन पास व्यवस्था)</h4>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {activeSlotInfo && (
                      <span
                        className={`live-indicator-badge ${activeSlotInfo.isActive ? 'active' : 'inactive'}`}
                      >
                        {activeSlotInfo.isActive && <span className="live-pulse-dot" />}
                        {activeSlotInfo.isActive ? `🟢 LIVE: ${activeSlotInfo.name}` : `⚪ ${activeSlotInfo.name}`}
                      </span>
                    )}

                    {/* Temple Trust Filter Dropdown */}
                    {trustList.length > 0 && (
                      <div className="d-flex align-items-center gap-1 ms-1">
                        <span className="text-muted small fw-semibold">🏛️ Trust:</span>
                        <Input
                          type="select"
                          bsSize="sm"
                          style={{ minWidth: '180px', height: '31px', fontSize: '13px' }}
                          value={selectedTrustId}
                          onChange={(e) => setSelectedTrustId(e.target.value)}
                          disabled={trustList.length === 1}
                        >
                          {trustList.length > 1 && (
                            <option value="">All Permitted Trusts (सभी अनुमत ट्रस्ट)</option>
                          )}
                          {trustList.map((trust) => (
                            <option key={trust.trustId} value={trust.trustId}>
                              {trust.trustName}
                            </option>
                          ))}
                        </Input>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 meal-pass-top-actions">
                <Button color="outline-secondary" size="sm" onClick={triggerRefresh}>
                  <RefreshCw size={14} className="me-1" /> Refresh
                </Button>
                <Button color="primary" onClick={() => setCreateModalOpen(true)}>
                  <PlusCircle size={16} className="me-1" /> Issue New Bhojan Pass
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Tab Switcher */}
        <Nav tabs className="mb-3 border-bottom meal-pass-tabs">
          <NavItem>
            <NavLink
              className={`py-2 px-3 fw-semibold ${activeTab === 'report' ? 'active text-primary border-primary' : 'text-muted'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setActiveTab('report')}
            >
              <BarChart2 size={16} className="me-1" /> Kitchen Data Report (दैनिक भोजन रिपोर्ट)
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`py-2 px-3 fw-semibold ${activeTab === 'list' ? 'active text-primary border-primary' : 'text-muted'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setActiveTab('list')}
            >
              <List size={16} className="me-1" /> Issued Passes List (जारी किए गए भोजन पास)
            </NavLink>
          </NavItem>
        </Nav>

        {/* Tab Contents */}
        {activeTab === 'report' && (
          <MealPassReport
            refreshSignal={refreshSignal}
            selectedTrustId={selectedTrustId}
          />
        )}
        {activeTab === 'list' && (
          <MealPassList
            refreshSignal={refreshSignal}
            selectedTrustId={selectedTrustId}
            trustList={trustList}
          />
        )}

        {/* Issue Meal Pass Modal */}
        <CreateMealPassModal
          isOpen={createModalOpen}
          toggle={() => setCreateModalOpen(!createModalOpen)}
          selectedTrustId={selectedTrustId}
          trustList={trustList}
          onSuccess={(newPass) => {
            triggerRefresh();
            if (newPass) {
              setCreatedPass(newPass);
              setDetailModalOpen(true);
            }
          }}
        />

        {/* View Pass QR Details Modal */}
        <MealPassDetailModal
          isOpen={detailModalOpen}
          toggle={() => setDetailModalOpen(false)}
          mealPass={createdPass}
        />
      </Container>
    </div>
  );
};

export default MealPass;
